import { HTMLAttributes, useEffect, useState } from 'react'
import QRCode from 'qrcode.react'
import styles from './styles.module.scss'
import Image from 'next/image'

import AppleIcon from './icons/apple.png'
import PlaystoreIcon from './icons/playstore.png'
import QRIcon from './icons/qr.png'
import GooglePlayIcon from './icons/google-play.png'
import CoinrabbitIcon from './icons/coinrabbit.png'

import { useTranslation } from 'utils/use-translation'
import ReactClickOutside from '@coxy/react-click-outside'

import * as ga from 'libs/ga'

import cn from 'classnames'
import Tooltip from '../tooltip'
import useViewport from 'libs/use-viewport'

interface MobileAppBtnProps extends HTMLAttributes<HTMLDivElement> {
    type?: 'primary'
    withQRCode?: boolean
}

const MobileAppBtn = ({
    type = 'primary',
    withQRCode = true,
}: MobileAppBtnProps) => {
    const { t } = useTranslation()
    const [view] = useViewport()
    const isMobile = ['xs-mobile', 'mobile'].includes(view)

    const [platform, setPlatform] = useState<'ios' | 'android' | 'unknown'>('unknown')
    const [isClicked, setIsClicked] = useState(false)
    const [isQRClicked, setQRClicked] = useState(false)
    const [isQRVisible, setIsQRVisible] = useState(false)

    useEffect(() => {
        if (typeof navigator === 'undefined') return

        const ua = navigator.userAgent.toLowerCase()

        if (ua.includes('android')) {
            setPlatform('android')
        } else if (/iphone|ipad|ipod/.test(ua)) {
            // todo
            setPlatform('unknown')
        } else {
            setPlatform('unknown')
        }
    }, [])

    const handleClick = () => {
        setIsClicked(true)

        ga.event({
            action: 'click_download_app_button',
        })

        setTimeout(() => setIsClicked(false), 100)
    }

    const showQR = () => {
        setIsQRVisible(prev => !prev)
        setQRClicked(true)

        setTimeout(() => setQRClicked(false), 100)
    }

    const icon = platform === 'ios' ? AppleIcon : PlaystoreIcon
    const topText = platform === 'ios' ? t('download_on_the') : t('get_it_on')
    const bottomText = platform === 'ios'
        ? <span className={cn({
            [styles['text__narrow']]: platform === 'ios'
        })}>{t('app_store')}</span>
        : <div className={styles.icon} style={{ height: '15px' }}>
            <Image
                src={GooglePlayIcon}
                width={74}
                height={15}
                alt="google play icon" />
        </div>

    const link = platform === 'ios'
        ? 'https://play.google.com/store/apps/details?id=com.coinrabbit'
        : 'https://play.google.com/store/apps/details?id=com.coinrabbit'

    return (
        <section className={styles.container}>
            <a href={link} target="_blank" rel="noreferrer"
                onMouseUp={handleClick}
                className={cn(styles.btn, styles.btn__download, {
                    [styles.btn__clicked]: isClicked,
                })}
            >
                <div className={styles.icon}>
                    <Image
                        src={icon}
                        width={platform === 'ios' ? 20 : 21}
                        height={24}
                        alt="store icon" />
                </div>

                <div className={cn(styles['btn__download-text'], {
                    [styles['btn__download-text-ios']]: platform === 'ios',
                })}>
                    <span className={cn({
                        [styles['text__uppercase']]: platform !== 'ios',
                        [styles['text__small']]: platform === 'ios',
                    })}>{topText}</span>

                    {bottomText}
                </div>
            </a>

            {!isMobile && <button
                onClick={showQR}
                className={cn(styles.btn, styles.btn__qr, styles.hasTooltip, {
                    [styles.btn__clicked]: isQRClicked,
                })}
            >
                <Image src={QRIcon} width={32} height={32} alt="qr icon" />

                <ReactClickOutside onClose={() => setIsQRVisible(false)} visible={isQRVisible}>
                    <Tooltip className={cn(styles.tooltip, {
                        [styles.tooltip__visible]: isQRVisible,
                    })}>
                        <QRCode value={link} size={132} className={styles.qr} />

                        <span>{t('coinrabbit_app')}</span>

                        <div className={styles.tooltip__logo}>
                            <Image
                                src={CoinrabbitIcon}
                                width={24}
                                height={24}
                            />
                        </div>
                    </Tooltip>
                </ReactClickOutside>
            </button>}
        </section>
    )
}

export default MobileAppBtn